import { env } from "../env";
import { getActingTenantId, getIdToken, setRefreshedToken } from "../auth/session";
import { refreshSession } from "../auth/cognito";

// Thin API client. Every request:
//  - includes Authorization: Bearer <id-token>.
//  - generates a client-side request id so server logs stitch with
//    front-end errors.
//  - on 401 triggers POST /auth/refresh (which reads the httpOnly
//    refresh-token cookie server-side) and retries once.
//
// Keep this file small. Feature modules import `apiFetch` and do
// their own JSON handling.

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    public readonly requestId: string,
  ) {
    super(`API ${status}`);
  }
}

function newRequestId(): string {
  return crypto.randomUUID();
}

async function doFetch(path: string, init: RequestInit, requestId: string): Promise<Response> {
  const token = getIdToken();
  const headers = new Headers(init.headers ?? {});
  headers.set("X-Request-ID", requestId);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  const actingTenantId = getActingTenantId();
  if (actingTenantId && !path.startsWith("/api/v1/admin/") && !headers.has("X-Acting-Tenant-ID")) {
    headers.set("X-Acting-Tenant-ID", actingTenantId);
  }
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  // No `credentials: 'include'` here. The API authenticates via the
  // bearer token, never cookies, so attaching credentials buys
  // nothing — and would force CORS to use an exact `Allow-Origin`
  // (browsers reject `Access-Control-Allow-Origin: *` on credentialed
  // requests). The one place credentials ARE needed is the explicit
  // `/auth/refresh` call below, which sets it directly.
  return fetch(`${env.apiBaseUrl}${path}`, {
    ...init,
    headers,
  });
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const requestId = newRequestId();
  let res = await doFetch(path, init, requestId);

  if (res.status === 401) {
    const refreshed = await refreshSession();
    if (refreshed) {
      res = await doFetch(path, init, requestId);
    } else {
      setRefreshedToken(null);
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body, requestId);
  }

  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}
